/**
 * IndexedDB-backed persistence for the web screen lock.
 *
 * Three records in one store ("app_lock") — mirrors iOS's three files:
 *
 *   key "passphrase_hash"  — AppLockPassphraseHash  (salt + hash + algorithm + ts)
 *   key "status"           — AppLockStatus          (state + counters + lastUnlockedAt)
 *   key "config"           — AppLockConfig          (backgroundLockMs)
 *
 * Why three records instead of one blob:
 *   - Status changes on every wrong passphrase attempt; hash is essentially
 *     immutable until the user changes their passphrase. Keeping them separate
 *     avoids overwriting the hash on every wrong attempt.
 *   - Config changes almost never and is read on page load.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AppLockStore.swift`.
 *
 * Thread / concurrency model:
 *   - All writes go through `IDBTransaction` which is already serialised by
 *     the browser's IDB implementation; no additional mutex needed.
 *   - Multiple AppLockStore instances at the same `dbName` share the same
 *     underlying IDB database and are safe to use concurrently — IDB's
 *     transaction model handles it.
 */

import type { AppLockPassphraseHash } from "./AppLockPin";
import { hashPassphrase, generateSalt } from "./AppLockPin";
import type { AppLockStatus } from "./AppLockState";
import {
  initialStatus,
  setBackgroundLockMs,
} from "./AppLockState";

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface AppLockConfig {
  backgroundLockMs: number;
}

// Serialisable form stored in IDB. Arrays become plain arrays over the wire;
// we convert Uint8Array ↔ number[] for JSON-compatible IDB storage.
interface StoredPassphraseHash {
  salt: number[];
  hash: number[];
  createdAt: string;
  algorithm: string;
  iterations: number;
}

// ---------------------------------------------------------------------------
// DB helpers
// ---------------------------------------------------------------------------

const STORE_NAME = "app_lock";
const HASH_KEY   = "passphrase_hash";
const STATUS_KEY = "status";
const CONFIG_KEY = "config";

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function dbGet<T>(db: IDBDatabase, key: string): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror   = () => reject(req.error);
  });
}

async function dbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

async function dbDelete(db: IDBDatabase, key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).delete(key);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Serialisation helpers
// ---------------------------------------------------------------------------

function encodeHash(h: AppLockPassphraseHash): StoredPassphraseHash {
  return {
    salt:      Array.from(h.salt),
    hash:      Array.from(h.hash),
    createdAt: h.createdAt,
    algorithm: h.algorithm,
    iterations: h.iterations,
  };
}

function decodeHash(s: StoredPassphraseHash): AppLockPassphraseHash {
  return {
    salt:       new Uint8Array(s.salt),
    hash:       new Uint8Array(s.hash),
    createdAt:  s.createdAt,
    algorithm:  s.algorithm as AppLockPassphraseHash["algorithm"],
    iterations: s.iterations,
  };
}

// ---------------------------------------------------------------------------
// AppLockStore
// ---------------------------------------------------------------------------

/**
 * Persisted store for the screen-lock state.
 *
 * Usage:
 *
 * ```ts
 * const store = new AppLockStore();
 * await store.init();               // opens IDB; loads config into state machine
 * await store.setPassphrase("hunter2");
 * const status = await store.currentStatus();
 * ```
 *
 * Pass `dbName` to isolate instances in tests.
 */
export class AppLockStore {
  private readonly dbName: string;
  private db: IDBDatabase | null = null;

  constructor(dbName = "kaether_app_lock") {
    this.dbName = dbName;
  }

  /**
   * Open (or create) the IndexedDB database. Must be called before any
   * other method. Safe to call multiple times — subsequent calls are no-ops.
   *
   * Also pushes the persisted background-lock threshold into the in-memory
   * state machine so subsequent `appForegroundedAfterMs` events use the
   * right value immediately.
   */
  async init(): Promise<void> {
    if (this.db) return;
    this.db = await openDb(this.dbName);
    // Mirror persisted config into the state machine.
    const ms = await this.currentBackgroundLockMs();
    setBackgroundLockMs(ms);
  }

  // --------------------------------------------------------------------------
  // Passphrase hash
  // --------------------------------------------------------------------------

  /**
   * Return the persisted hash, or `undefined` if the user has never set a
   * passphrase (→ show the "set up your passphrase" onboarding screen).
   */
  async currentHash(): Promise<AppLockPassphraseHash | undefined> {
    const stored = await dbGet<StoredPassphraseHash>(this._db(), HASH_KEY);
    return stored ? decodeHash(stored) : undefined;
  }

  /**
   * Set a new passphrase. Generates a fresh 32-byte salt every call so
   * changing the passphrase doesn't reuse the old salt.
   *
   * @throws PassphraseTooShortError / PassphraseTooLongError
   */
  async setPassphrase(
    passphrase: string,
    now: Date = new Date(),
  ): Promise<void> {
    const salt = generateSalt();
    const hashed = await hashPassphrase(passphrase, salt, undefined, now);
    await dbPut(this._db(), HASH_KEY, encodeHash(hashed));
  }

  /**
   * Remove the persisted hash. Used by the wipe path and by users who
   * disable the screen lock.
   */
  async clearPassphrase(): Promise<void> {
    await dbDelete(this._db(), HASH_KEY);
  }

  // --------------------------------------------------------------------------
  // Status (state + attempt counters)
  // --------------------------------------------------------------------------

  /**
   * Return the persisted status, or a freshly-locked status if nothing was
   * on disk. Cold-launch always returns `lockedRequirePassphrase`.
   */
  async currentStatus(): Promise<AppLockStatus> {
    const stored = await dbGet<AppLockStatus>(this._db(), STATUS_KEY);
    return stored ?? initialStatus(0);
  }

  /** Persist a new status. Caller is the state machine. */
  async updateStatus(status: AppLockStatus): Promise<void> {
    await dbPut(this._db(), STATUS_KEY, status);
  }

  // --------------------------------------------------------------------------
  // Config (background-lock threshold)
  // --------------------------------------------------------------------------

  /** Returns the persisted threshold in ms, or 60_000 (1 minute) if unset. */
  async currentBackgroundLockMs(): Promise<number> {
    const cfg = await dbGet<AppLockConfig>(this._db(), CONFIG_KEY);
    return cfg?.backgroundLockMs ?? 60_000;
  }

  /**
   * Persist a new threshold AND mirror it into the in-memory state machine
   * so subsequent `appForegroundedAfterMs` events use it immediately.
   *
   * Presets: 0 (immediate), 60_000 (1m), 300_000 (5m), 1_800_000 (30m),
   * 3_600_000 (1h), -1 (never).
   */
  async setBackgroundLockMs(ms: number): Promise<void> {
    await dbPut(this._db(), CONFIG_KEY, { backgroundLockMs: ms } satisfies AppLockConfig);
    setBackgroundLockMs(ms);
  }

  // --------------------------------------------------------------------------
  // Wipe
  // --------------------------------------------------------------------------

  /**
   * Remove every artefact this store wrote. Called by the wipe path when
   * `wipePending` is observed. Higher layers (the app root) are responsible
   * for the rest of the wipe (SignalStore, message database, identity keys).
   * This method only clears the app-lock records.
   */
  async wipeAllData(): Promise<void> {
    const db = this._db();
    await Promise.all([
      dbDelete(db, HASH_KEY),
      dbDelete(db, STATUS_KEY),
      dbDelete(db, CONFIG_KEY),
    ]);
  }

  // --------------------------------------------------------------------------
  // Internal
  // --------------------------------------------------------------------------

  private _db(): IDBDatabase {
    if (!this.db) {
      throw new Error("AppLockStore: call init() before using the store");
    }
    return this.db;
  }
}

/** Singleton for production use. Tests create their own instances. */
export const sharedAppLockStore = new AppLockStore();
